import { useLazyQuery, useMutation } from '@apollo/client';

import { ADD_INGREDIENT, ADD_INSTRUCTION, ADD_OUTSIDE_INSTRUCTION, ADD_PRESET, INGREDIENT_TO_INGREDIENT, INGREDIENT_TO_INSTRUCTION, INGREDIENT_TO_OUTSIDE_INSTRUCTION, INGREDIENT_TO_PRESET, INITIALIZE_INGREDIENT, INITIALIZE_INSTRUCTION, INITIALIZE_OUTSIDE_INSTRUCTION, INITIALIZE_PRESET, INSTRUCTION_TO_INGREDIENT, INSTRUCTION_TO_INSTRUCTION, INSTRUCTION_TO_OUTSIDE_INSTRUCTION, INSTRUCTION_TO_PRESET, OUTSIDE_INSTRUCTION_TO_INGREDIENT, OUTSIDE_INSTRUCTION_TO_INSTRUCTION, OUTSIDE_INSTRUCTION_TO_OUTSIDE_INSTRUCTION, OUTSIDE_INSTRUCTION_TO_PRESET, PRESET_TO_INGREDIENT, PRESET_TO_INSTRUCTION, PRESET_TO_OUTSIDE_INSTRUCTION, PRESET_TO_PRESET, UPDATE_RECIPE_LAST_UPDATED_AT_AND_TAGS } from '../app/graphql/mutations';
import { Recipe, Substep } from "../app/recipeCreation/createRecipe.types";
import cookingTimeCalculation from '../utils/cookingTimeUtil';
import { chooseSaveTypeChange, generateIngredientImages, getAddedandRemovedIngredients, getSelectedRecipeVariation, getSelectedRecipeVariationSteps, getSelectedStep, getUpdatedCookingTime, getUpdatedStepSubSteps, ingredientInsertsVals, initializeStepData, removeAndAddIngredientUpdatedIngredientIdList, removeIngredientUpdatedIngredientIdList, updateRecipeLastUpdatedAtAndTags } from './utils/useSaveSubstep.utils';
import { useAppSelector } from '../app/store/hooks';
import { ingredientsObjectSelector } from '../app/commonSelector';
import { ensureIngredientsObject, getNutrientInfo } from '../utils/experience2/ingredientsUtil';
import { IngredientsInterface } from '../app/homepage/homepage.types';
import { GET_RECIPE_INGREDIENTS } from '../app/graphql/queries';

const addPreset = async ({
  oldRecipe,
  recipeVariationId,
  stepId,
  addSubStepAt,
  newSubstepId,
  newSubstep,
  addPresetMutation
}: {

  oldRecipe: Recipe,
  stepId: string,
  recipeVariationId: string,
  addSubStepAt: number,
  newSubstepId: string,
  newSubstep: Substep,
  addPresetMutation: any
}) => {
  const recipeVariationSteps = getSelectedRecipeVariationSteps(oldRecipe, recipeVariationId);
  const recipeVarStep = getSelectedStep(recipeVariationSteps, stepId);
  const updatedCookingTime: number = getUpdatedCookingTime(recipeVariationSteps, newSubstep);
  const { updatedStepSubsteps, insertStepSubstep } = getUpdatedStepSubSteps(recipeVarStep, addSubStepAt, stepId, newSubstepId);
  await addPresetMutation({
    variables: {
      stepSubstepUpdates: updatedStepSubsteps,
      recipeVariationId,
      cooking_time: updatedCookingTime,
      stepSubstepsInserts: insertStepSubstep,
    }
  })
}

const addInstruction = async ({
  oldRecipe,
  recipeVariationId,
  stepId,
  addSubStepAt,
  newSubstepId,
  newSubstep,
  addInstructionMutation
}: {
  oldRecipe: Recipe,
  stepId: string,
  recipeVariationId: string,
  addSubStepAt: number,
  newSubstepId: string,
  newSubstep: Substep,
  addInstructionMutation: any
}) => {
  const recipeVariationSteps = getSelectedRecipeVariationSteps(oldRecipe, recipeVariationId);
  const recipeVarStep = getSelectedStep(recipeVariationSteps, stepId);

  const updatedCookingTime: number = getUpdatedCookingTime(recipeVariationSteps, newSubstep);
  const { updatedStepSubsteps, insertStepSubstep } = getUpdatedStepSubSteps(recipeVarStep, addSubStepAt, stepId, newSubstepId);
  const { Recipe_Presets, SubSteps_Ingredients_E3s, ...substepInsert } = newSubstep;

  await addInstructionMutation({
    variables: {
      substepInserts: substepInsert,
      stepSubstepsInserts: insertStepSubstep,
      stepSubstepUpdates: updatedStepSubsteps,
      recipeVariationId,
      cooking_time: updatedCookingTime
    }
  })

}

const addIngredient = async ({
  oldRecipe,
  recipeVariationId,
  stepId,
  addSubStepAt,
  newSubstepId,
  newSubstep,
  addIngredientMutation,
  fetchRecipeIngredients,
  ingredientsObject
}: {
  oldRecipe: Recipe,
  stepId: string,
  recipeVariationId: string,
  addSubStepAt: number,
  newSubstepId: string,
  newSubstep: Substep,
  addIngredientMutation: any,
  fetchRecipeIngredients: any,
  ingredientsObject: IngredientsInterface
}) => {
  const selectedRecipeVariation = getSelectedRecipeVariation(oldRecipe, recipeVariationId);
  const oldRecipeVariationIngredientList = selectedRecipeVariation?.ingredients ? [...selectedRecipeVariation.ingredients] : [];
  const recipeVariationSteps = getSelectedRecipeVariationSteps(oldRecipe, recipeVariationId);
  const recipeVarStep = getSelectedStep(recipeVariationSteps, stepId);
  
  const updatedIngredientsObject = await ensureIngredientsObject(
    ingredientsObject,
    newSubstep,
    fetchRecipeIngredients
  );

  const currentMetadata = selectedRecipeVariation?.metadata || {};
  const updatedNutrientInfo = getNutrientInfo(oldRecipe, updatedIngredientsObject, newSubstep, null);
  const updatedMetadata = { ...currentMetadata, nutritionalValues: updatedNutrientInfo};
  const { updatedStepSubsteps, insertStepSubstep } = getUpdatedStepSubSteps(recipeVarStep, addSubStepAt, stepId, newSubstepId);
  const { newIngredientsList, ...parametersWithoutIngredientList } = newSubstep.parameters;
  const { Recipe_Presets, SubSteps_Ingredients_E3s: substepIngredientInsert, ...substepInsert } = newSubstep;
  substepInsert.parameters = parametersWithoutIngredientList;
  substepIngredientInsert.forEach((substepIngredient) => {
    oldRecipeVariationIngredientList.push(substepIngredient.ingredient_id);
  })
  const ingredientIdList = Array.from(new Set(oldRecipeVariationIngredientList));

  generateIngredientImages(newIngredientsList)
  const ingredientsInsert = ingredientInsertsVals(newIngredientsList)

  await addIngredientMutation({
    variables: {
      substepInserts: substepInsert,
      stepSubstepsInserts: insertStepSubstep,
      stepSubstepUpdates: updatedStepSubsteps,
      ingredientsInsert,
      recipeVariationId,
      ingredientIdList,
      substepIngredients: substepIngredientInsert,
      updatedMetadata,
    }
  })
}

const addOutsideInstruction = async ({
  oldRecipe,
  recipeVariationId,
  stepId,
  addSubStepAt,
  newSubstepId,
  newSubstep,
  addOutsideInstructionMutation
}: {
  oldRecipe: Recipe,
  stepId: string,
  recipeVariationId: string,
  addSubStepAt: number,
  newSubstepId: string,
  newSubstep: Substep,
  addOutsideInstructionMutation: any
}) => {
  const recipeVariationSteps = getSelectedRecipeVariationSteps(oldRecipe, recipeVariationId);
  const recipeVarStep = getSelectedStep(recipeVariationSteps, stepId);

  const { updatedStepSubsteps, insertStepSubstep } = getUpdatedStepSubSteps(recipeVarStep, addSubStepAt, stepId, newSubstepId);
  const { Recipe_Presets, SubSteps_Ingredients_E3s, ...substepInsert } = newSubstep;
  await addOutsideInstructionMutation({
    variables: {
      substepInserts: substepInsert,
      stepSubstepsInserts: insertStepSubstep,
      stepSubstepUpdates: updatedStepSubsteps,
    }
  })
}

const initializePreset = async ({
  stepId,
  newSubstepId,
  recipeVariationId,
  newSubstep,
  initializePresetMutation
}: {
  stepId: string,
  newSubstepId: string,
  recipeVariationId: string,
  newSubstep: Substep,
  initializePresetMutation: any
}) => {
  const { stepDataInsert, recipeVarStepsInsert, stepSubstepInsert } = initializeStepData(stepId, recipeVariationId, newSubstepId)

  const updatedCookingTime = Math.ceil(cookingTimeCalculation([newSubstep]) / 60);

  await initializePresetMutation({
    variables: {
      stepDataInsert,
      recipeVarStepsInsert,
      stepSubstepInsert,
      recipeVariationId,
      cooking_time: updatedCookingTime
    }
  })

}

const initializeInstruction = async ({
  stepId,
  newSubstepId,
  recipeVariationId,
  newSubstep,
  initializeInstructionMutation
}: {
  stepId: string,
  newSubstepId: string,
  recipeVariationId: string,
  newSubstep: Substep,
  initializeInstructionMutation: any
}) => {
  const { stepDataInsert, recipeVarStepsInsert, stepSubstepInsert } = initializeStepData(stepId, recipeVariationId, newSubstepId)

  const updatedCookingTime = Math.ceil(cookingTimeCalculation([newSubstep]) / 60);
  const { Recipe_Presets, SubSteps_Ingredients_E3s, ...substepInsert } = newSubstep;

  await initializeInstructionMutation({
    variables: {
      stepDataInsert,
      recipeVarStepsInsert,
      stepSubstepInsert,
      recipeVariationId,
      cooking_time: updatedCookingTime,
      newSubstep: substepInsert
    }
  })
}

const initializeIngredient = async (
  { oldRecipe,
    recipeVariationId,
    stepId,
    newSubstepId,
    newSubstep,
    initializeIngredientMutation,
    fetchRecipeIngredients,
    ingredientsObject

  }: {
    oldRecipe: Recipe,
    stepId: string,
    recipeVariationId: string,
    newSubstepId: string,
    newSubstep: Substep,
    initializeIngredientMutation: any,
    fetchRecipeIngredients: any,
    ingredientsObject: IngredientsInterface

  }
) => {
  const selectedRecipeVariation = getSelectedRecipeVariation(oldRecipe, recipeVariationId);
  const oldRecipeVariationIngredientList = selectedRecipeVariation?.ingredients ? [...selectedRecipeVariation.ingredients] : [];
  const currentMetadata = selectedRecipeVariation?.metadata || {};
  const updatedIngredientsObject = await ensureIngredientsObject(
    ingredientsObject,
    newSubstep,
    fetchRecipeIngredients
  );
  const updatedNutrientInfo = getNutrientInfo(oldRecipe, updatedIngredientsObject, newSubstep, null);
  const updatedMetadata = { ...currentMetadata, nutritionalValues: updatedNutrientInfo};

  const { stepDataInsert, recipeVarStepsInsert, stepSubstepInsert } = initializeStepData(stepId, recipeVariationId, newSubstepId)

  const { newIngredientsList, ...parametersWithoutIngredientList } = newSubstep.parameters;

  const { Recipe_Presets, SubSteps_Ingredients_E3s: substepIngredientInsert, ...substepInsert } = newSubstep;
  substepInsert.parameters = parametersWithoutIngredientList;

  substepIngredientInsert.forEach((substepIngredient) => {
    oldRecipeVariationIngredientList.push(substepIngredient.ingredient_id);
  })
  const ingredientIdList = Array.from(new Set(oldRecipeVariationIngredientList));

  generateIngredientImages(newIngredientsList)

  const ingredientsInsert = ingredientInsertsVals(newIngredientsList)

  await initializeIngredientMutation({
    variables: {
      stepDataInsert,
      recipeVarStepsInsert,
      substepInsert,
      stepSubstepsInserts: stepSubstepInsert,
      ingredientsInsert,
      recipeVariationId,
      ingredientIdList,
      substepIngredients: substepIngredientInsert,
      updatedMetadata,
    }
  })
};

const initializeOutsideInstruction = async ({
  stepId,
  newSubstepId,
  recipeVariationId,
  newSubstep,
  initializeOutsideInstructionMutation
}: {
  stepId: string,
  newSubstepId: string,
  recipeVariationId: string,
  newSubstep: Substep,
  initializeOutsideInstructionMutation: any
}) => {
  const { stepDataInsert, recipeVarStepsInsert, stepSubstepInsert } = initializeStepData(stepId, recipeVariationId, newSubstepId);
  const { Recipe_Presets, SubSteps_Ingredients_E3s, ...substepInsert } = newSubstep;

  await initializeOutsideInstructionMutation({
    variables: {
      newSubstep: substepInsert,
      stepDataInsert,
      recipeVarStepsInsert,
      stepSubstepInsert
    }
  });
}

const ingredientsTOinstruction = async ({
  oldRecipe,
  recipeVariationId,
  oldSubstepId,
  subStepUId,
  newSubstep,
  oldSubstep,
  updateIngredientTOinstruction,
  ingredientsObject
}: {
  oldRecipe: Recipe,
  recipeVariationId: string,
  oldSubstepId: string,
  subStepUId: string,
  newSubstep: Substep,
  oldSubstep: Substep,
  updateIngredientTOinstruction: any,
  ingredientsObject: IngredientsInterface
}) => {
  const selectedRecipeVariation = getSelectedRecipeVariation(oldRecipe, recipeVariationId);
  const updatedCookingTime = selectedRecipeVariation && selectedRecipeVariation?.cooking_time + Math.ceil(cookingTimeCalculation([newSubstep]) / 60);
  const substepIngredientIds = oldSubstep.SubSteps_Ingredients_E3s.map((substepIngredient) => substepIngredient.id);
  const updatedIngredientsList = removeIngredientUpdatedIngredientIdList(oldRecipe, recipeVariationId, subStepUId);
  const { Recipe_Presets, SubSteps_Ingredients_E3s: substepIngredientInsert, ...substepInsert } = newSubstep;
  const currentMetadata = selectedRecipeVariation?.metadata || {};
  const updatedNutrientInfo = getNutrientInfo(oldRecipe, ingredientsObject, newSubstep, oldSubstep);
  const updatedMetadata = { ...currentMetadata, nutritionalValues: updatedNutrientInfo};
  await updateIngredientTOinstruction({
    variables: {
      substepId: oldSubstepId,
      newSubstep: substepInsert,
      substepIngredientIds,
      recipeVariationId,
      cooking_time: updatedCookingTime,
      newIngredientsList: updatedIngredientsList,
      updatedMetadata
    }
  })
}

const ingredientsTOpreset = async ({
  oldRecipe,
  recipeVariationId,
  oldSubstepId,
  newSubstepId,
  newSubstep,
  oldSubstep,
  stepId,
  subStepUId,
  updateIngredientTOpreset,
  ingredientsObject
}: {
  oldRecipe: Recipe,
  recipeVariationId: string,
  oldSubstepId: string,
  newSubstepId: string,
  newSubstep: Substep,
  oldSubstep: Substep,
  stepId: string,
  subStepUId: string,
  updateIngredientTOpreset: any,
  ingredientsObject: IngredientsInterface
}) => {
  const selectedRecipeVariation = getSelectedRecipeVariation(oldRecipe, recipeVariationId);
  const recipeVariationSteps = getSelectedRecipeVariationSteps(oldRecipe, recipeVariationId);
  const recipeVarStep = getSelectedStep(recipeVariationSteps, stepId);
  const updatedCookingTime = selectedRecipeVariation && selectedRecipeVariation?.cooking_time + Math.ceil(cookingTimeCalculation([newSubstep]) / 60);
  const updatedIngredientsList = removeIngredientUpdatedIngredientIdList(oldRecipe, recipeVariationId, subStepUId);
  const substepIngredientIds = oldSubstep.SubSteps_Ingredients_E3s.map((substepIngredient) => substepIngredient.id);
  const currentMetadata = selectedRecipeVariation?.metadata || {};
  const updatedNutrientInfo = getNutrientInfo(oldRecipe, ingredientsObject, newSubstep, oldSubstep);
  const updatedMetadata = { ...currentMetadata, nutritionalValues: updatedNutrientInfo};
  
  if (recipeVarStep) {
    const stepSubStepToUpdate = recipeVarStep.StepsE3.Steps_Substeps.find((stepSubstep) => stepSubstep.id === subStepUId);
    if (stepSubStepToUpdate) {
      const stepSubstepId = stepSubStepToUpdate.id;
      await updateIngredientTOpreset({
        variables: {
          stepSubstepId,
          newSubstepId,
          oldSubstepId,
          recipeVariationId,
          cooking_time: updatedCookingTime,
          newIngredientsList: updatedIngredientsList,
          substepIngredientIds,
          updatedMetadata
        }
      })
    }
  }

}

const ingredientsTOoutsideInstruction = async ({
  oldRecipe,
  recipeVariationId,
  oldSubstepId,
  subStepUId,
  newSubstep,
  oldSubstep,
  updateIngredientTOoutsideInstruction,
  ingredientsObject
}: {
  oldRecipe: Recipe,
  recipeVariationId: string,
  oldSubstepId: string,
  subStepUId: string,
  newSubstep: Substep,
  oldSubstep: Substep,
  updateIngredientTOoutsideInstruction: any,
  ingredientsObject: IngredientsInterface
}) => {
  const substepIngredientIds = oldSubstep.SubSteps_Ingredients_E3s.map((substepIngredient) => substepIngredient.id);
  const updatedIngredientsList = removeIngredientUpdatedIngredientIdList(oldRecipe, recipeVariationId, subStepUId);
  const { Recipe_Presets, SubSteps_Ingredients_E3s: substepIngredientInsert, ...substepInsert } = newSubstep;
  
  const selectedRecipeVariation = getSelectedRecipeVariation(oldRecipe, recipeVariationId);
  const currentMetadata = selectedRecipeVariation?.metadata || {};
  const updatedNutrientInfo = getNutrientInfo(oldRecipe, ingredientsObject, newSubstep, oldSubstep);
  const updatedMetadata = { ...currentMetadata, nutritionalValues: updatedNutrientInfo};
  
  await updateIngredientTOoutsideInstruction({
    variables: {
      substepId: oldSubstepId,
      newSubstep: substepInsert,
      substepIngredientIds,
      recipeVariationId,
      newIngredientsList: updatedIngredientsList,
      updatedMetadata
    }
  })
}

const ingredientsTOingredients = async ({
  oldRecipe,
  recipeVariationId,
  oldSubstepId,
  subStepUId,
  newSubstep,
  oldSubstep,
  updateIngredientTOingredients,
  ingredientsObject
}: {
  oldRecipe: Recipe,
  recipeVariationId: string,
  oldSubstepId: string,
  subStepUId: string,
  newSubstep: Substep,
  oldSubstep: Substep,
  updateIngredientTOingredients: any,
  ingredientsObject: IngredientsInterface
}) => {
  const oldSubstepIngredients = oldSubstep.SubSteps_Ingredients_E3s;
  const newSubstepIngredients = newSubstep.SubSteps_Ingredients_E3s;
  const { deleted: substepIngredientIdsToDelete, added: substepIngredientIdsToAdd } = getAddedandRemovedIngredients(oldSubstepIngredients, newSubstepIngredients);

  const selectedRecipeVariation = getSelectedRecipeVariation(oldRecipe, recipeVariationId);
  const oldRecipeVariationIngredientList = selectedRecipeVariation?.ingredients ? [...selectedRecipeVariation.ingredients] : [];
  const currentMetadata = selectedRecipeVariation?.metadata || {};
  const updatedNutrientInfo = getNutrientInfo(oldRecipe, ingredientsObject, newSubstep, oldSubstep);
  const updatedMetadata = { ...currentMetadata, nutritionalValues: updatedNutrientInfo};
  
  const { newIngredientsList, ...parametersWithoutIngredientList } = newSubstep.parameters;
  const { Recipe_Presets, SubSteps_Ingredients_E3s: substepIngredientInsert, ...substepInsert } = newSubstep;
  substepInsert.parameters = parametersWithoutIngredientList;
  substepIngredientInsert.forEach((substepIngredient) => {
    oldRecipeVariationIngredientList.push(substepIngredient.ingredient_id);
  })
  const ingredientIdList = removeAndAddIngredientUpdatedIngredientIdList(oldRecipe, recipeVariationId, subStepUId, newSubstep);

  generateIngredientImages(newIngredientsList)

  const ingredientsInsert = ingredientInsertsVals(newIngredientsList)

  await updateIngredientTOingredients({
    variables: {
      substepId: oldSubstepId,
      newSubstep: substepInsert,
      ingredientsInsert,
      substepIngredientIdsToDelete,
      substepIngredientIdsToAdd,
      recipeVariationId,
      newIngredientsList: ingredientIdList,
      updatedMetadata
    }
  })
}

const instructionTOingredients = async ({
  oldSubstepId,
  oldSubstep,
  oldRecipe,
  recipeVariationId,
  newSubstep,
  updateInstructionToIngredents,
  fetchRecipeIngredients,
  ingredientsObject,
}: {
  oldSubstepId: string,
  oldSubstep: Substep,
  oldRecipe: Recipe,
  recipeVariationId: string,
  newSubstep: Substep,
  updateInstructionToIngredents: any,
  fetchRecipeIngredients: any,
  ingredientsObject: IngredientsInterface

}) => {
  const selectedRecipeVariation = getSelectedRecipeVariation(oldRecipe, recipeVariationId);
  const oldRecipeVariationIngredientList = selectedRecipeVariation?.ingredients ? [...selectedRecipeVariation.ingredients] : [];
  const updatedCookingTime = selectedRecipeVariation && selectedRecipeVariation?.cooking_time - Math.ceil(cookingTimeCalculation([oldSubstep]) / 60);
  const { newIngredientsList, ...parametersWithoutIngredientList } = newSubstep.parameters;
  const { Recipe_Presets, SubSteps_Ingredients_E3s: substepIngredientInsert, ...substepInsert } = newSubstep;
  substepInsert.parameters = parametersWithoutIngredientList;
  substepIngredientInsert.forEach((substepIngredient) => {
    oldRecipeVariationIngredientList.push(substepIngredient.ingredient_id);
  })
  const ingredientIdList = Array.from(new Set(oldRecipeVariationIngredientList));
  const currentMetadata = selectedRecipeVariation?.metadata || {};

  const updatedIngredientsObject = await ensureIngredientsObject(
    ingredientsObject,
    newSubstep,
    fetchRecipeIngredients
  );

  const updatedNutrientInfo = getNutrientInfo(oldRecipe, updatedIngredientsObject, newSubstep, oldSubstep);
  const updatedMetadata = { ...currentMetadata, nutritionalValues: updatedNutrientInfo};
  
  generateIngredientImages(newIngredientsList)

  const ingredientsInsert = ingredientInsertsVals(newIngredientsList)

  await updateInstructionToIngredents({
    variables: {
      oldSubstepId,
      newSubstep: substepInsert,
      ingredientsInsert,
      substepIngredientIdsToAdd: substepIngredientInsert,
      recipeVariationId,
      newIngredientsList: ingredientIdList,
      cooking_time: updatedCookingTime,
      updatedMetadata
    }
  })
}

const instructionTOpreset = async ({
  oldRecipe,
  recipeVariationId,
  stepId,
  oldSubstepId,
  newSubstepId,
  newSubstep,
  oldSubstep,
  subStepUId,
  updateInstructionToPreset
}: {
  oldRecipe: Recipe,
  recipeVariationId: string,
  stepId: string,
  oldSubstepId: string,
  newSubstepId: string,
  newSubstep: Substep,
  oldSubstep: Substep,
  subStepUId: string,
  updateInstructionToPreset: any
}) => {
  const selectedRecipeVariation = getSelectedRecipeVariation(oldRecipe, recipeVariationId);
  const recipeVariationSteps = getSelectedRecipeVariationSteps(oldRecipe, recipeVariationId);
  const recipeVarStep = getSelectedStep(recipeVariationSteps, stepId);

  if (recipeVarStep) {
    const stepSubStepToUpdate = recipeVarStep.StepsE3.Steps_Substeps.find((stepSubstep) => stepSubstep.id === subStepUId);

    if (stepSubStepToUpdate) {
      const cookingTimeOldSubstep = Math.ceil(cookingTimeCalculation([oldSubstep]) / 60);
      const cookingTimeNewSubstep = Math.ceil(cookingTimeCalculation([newSubstep]) / 60);
      const updatedCookingTime = selectedRecipeVariation ? selectedRecipeVariation.cooking_time - cookingTimeOldSubstep + cookingTimeNewSubstep : 0;

      await updateInstructionToPreset({
        variables: {
          subStep_id: newSubstepId,
          stepSubstepId: stepSubStepToUpdate.id,
          cooking_time: updatedCookingTime,
          substepIdToDelete: oldSubstepId,
          recipeVariationId
        }
      })
    }
  }
}

const instructionTOoutsideInstruction = async ({
  oldRecipe,
  recipeVariationId,
  oldSubstepId,
  newSubstep,
  oldSubstep,
  updateInstructionToOutsideInstruction
}: {
  oldRecipe: Recipe,
  recipeVariationId: string,
  oldSubstepId: string,
  newSubstep: Substep,
  oldSubstep: Substep,
  updateInstructionToOutsideInstruction: any
}) => {
  const cookingTimeOldSubstep = Math.ceil(cookingTimeCalculation([oldSubstep]) / 60);
  const selectedRecipeVariation = getSelectedRecipeVariation(oldRecipe, recipeVariationId);
  const updatedCookingTime = selectedRecipeVariation ? selectedRecipeVariation.cooking_time - cookingTimeOldSubstep : 0;
  const { Recipe_Presets, SubSteps_Ingredients_E3s, ...substepInsert } = newSubstep;


  await updateInstructionToOutsideInstruction({
    variables: {
      substepId: oldSubstepId,
      newSubstep: substepInsert,
      recipeVariationId,
      cooking_time: updatedCookingTime
    }
  })
}

const instructionTOinstruction = async ({
  oldRecipe,
  recipeVariationId,
  oldSubstepId,
  newSubstep,
  oldSubstep,
  updateInstructionToInstruction
}: {
  oldRecipe: Recipe,
  recipeVariationId: string,
  oldSubstepId: string,
  newSubstep: Substep,
  oldSubstep: Substep,
  updateInstructionToInstruction: any
}) => {
  const cookingTimeOldSubstep = Math.ceil(cookingTimeCalculation([oldSubstep]) / 60);
  const selectedRecipeVariation = getSelectedRecipeVariation(oldRecipe, recipeVariationId);
  const cookingTimeNewSubstep = Math.ceil(cookingTimeCalculation([newSubstep]) / 60);
  const updatedCookingTime = selectedRecipeVariation ? selectedRecipeVariation.cooking_time - cookingTimeOldSubstep + cookingTimeNewSubstep : 0;
  const { Recipe_Presets, SubSteps_Ingredients_E3s, ...substepInsert } = newSubstep;

  await updateInstructionToInstruction({
    variables: {
      substepId: oldSubstepId,
      newSubstep: substepInsert,
      recipeVariationId,
      cooking_time: updatedCookingTime
    }
  })
}

const outsideInstructionTOinstruction = async ({
  oldRecipe,
  recipeVariationId,
  oldSubstepId,
  newSubstep,
  updateOutsideInstructionToInstruction
}: {
  oldRecipe: Recipe,
  recipeVariationId: string,
  oldSubstepId: string,
  newSubstep: Substep,
  updateOutsideInstructionToInstruction: any
}) => {
  const cookingTimeNewSubstep = Math.ceil(cookingTimeCalculation([newSubstep]) / 60);
  const selectedRecipeVariation = getSelectedRecipeVariation(oldRecipe, recipeVariationId);
  const updatedCookingTime = selectedRecipeVariation ? selectedRecipeVariation.cooking_time + cookingTimeNewSubstep : 0;
  const { Recipe_Presets, SubSteps_Ingredients_E3s, ...substepInsert } = newSubstep;

  await updateOutsideInstructionToInstruction({
    variables: {
      substepId: oldSubstepId,
      newSubstep: substepInsert,
      recipeVariationId,
      cooking_time: updatedCookingTime
    }
  })
}

const outsideInstructionTOpreset = async ({
  oldRecipe,
  recipeVariationId,
  oldSubstepId,
  newSubstepId,
  newSubstep,
  stepId,
  subStepUId,
  updateOutsideInstructionToPreset
}: {
  oldRecipe: Recipe,
  recipeVariationId: string,
  oldSubstepId: string,
  newSubstepId: string,
  newSubstep: Substep,
  stepId: string,
  subStepUId: string,
  updateOutsideInstructionToPreset: any
}) => {
  const recipeVariationSteps = getSelectedRecipeVariationSteps(oldRecipe, recipeVariationId);
  const cookingTimeNewSubstep = Math.ceil(cookingTimeCalculation([newSubstep]) / 60);
  const selectedRecipeVariation = getSelectedRecipeVariation(oldRecipe, recipeVariationId);
  const updatedCookingTime = selectedRecipeVariation ? selectedRecipeVariation.cooking_time + cookingTimeNewSubstep : 0;
  const recipeVarStep = getSelectedStep(recipeVariationSteps, stepId);

  if (recipeVarStep) {
    const stepSubStepToUpdate = recipeVarStep.StepsE3.Steps_Substeps.find((stepSubstep) => stepSubstep.id === subStepUId);
    if (stepSubStepToUpdate) {
      await updateOutsideInstructionToPreset({
        variables: {
          stepSubstepId: stepSubStepToUpdate.id,
          subStep_id: newSubstepId,
          recipeVariationId,
          cooking_time: updatedCookingTime,
          substepIdToDelete: oldSubstepId
        }
      })
    }
  }
}

const outsideInstructionTOingredients = async ({
  oldSubstepId,
  oldRecipe,
  recipeVariationId,
  newSubstep,
  updateOutsideInstructionToIngredients,
  fetchRecipeIngredients,
  ingredientsObject
}: {
  oldSubstepId: string,
  oldRecipe: Recipe,
  recipeVariationId: string,
  newSubstep: Substep,
  updateOutsideInstructionToIngredients: any,
  fetchRecipeIngredients: any,
  ingredientsObject: IngredientsInterface
}) => {
  const selectedRecipeVariation = getSelectedRecipeVariation(oldRecipe, recipeVariationId);
  const oldRecipeVariationIngredientList = selectedRecipeVariation?.ingredients ? [...selectedRecipeVariation.ingredients] : [];
  const { newIngredientsList, ...parametersWithoutIngredientList } = newSubstep.parameters;
  const { Recipe_Presets, SubSteps_Ingredients_E3s: substepIngredientInsert, ...substepInsert } = newSubstep;
  substepInsert.parameters = parametersWithoutIngredientList;
  substepIngredientInsert.forEach((substepIngredient) => {
    oldRecipeVariationIngredientList.push(substepIngredient.ingredient_id);
  })
  const ingredientIdList = Array.from(new Set(oldRecipeVariationIngredientList));
  const currentMetadata = selectedRecipeVariation?.metadata || {};
  const updatedIngredientsObject = await ensureIngredientsObject(
    ingredientsObject,
    newSubstep,
    fetchRecipeIngredients
  );
  const updatedNutrientInfo = getNutrientInfo(oldRecipe, updatedIngredientsObject, newSubstep, null);
  const updatedMetadata = { ...currentMetadata, nutritionalValues: updatedNutrientInfo};
  
  generateIngredientImages(newIngredientsList)

  const ingredientsInsert = ingredientInsertsVals(newIngredientsList)
  await updateOutsideInstructionToIngredients({
    variables: {
      oldSubstepId,
      newSubstep: substepInsert,
      ingredientsInsert,
      substepIngredientIdsToAdd: substepIngredientInsert,
      recipeVariationId,
      newIngredientsList: ingredientIdList,
      updatedMetadata
    }
  })
}

const outsideInstructionTOoutsideInstruction = async ({
  oldSubstepId,
  newSubstep,
  updateOutsideInstructionToOutsideInstruction
}: {
  oldSubstepId: string,
  newSubstep: Substep,
  updateOutsideInstructionToOutsideInstruction: any
}) => {
  const { Recipe_Presets, SubSteps_Ingredients_E3s, ...substepInsert } = newSubstep;

  await updateOutsideInstructionToOutsideInstruction({
    variables: {
      substepId: oldSubstepId,
      newSubstep: substepInsert
    }
  })
}

const presetTOinstruction = async ({
  oldRecipe,
  recipeVariationId,
  stepId,
  oldSubstepId,
  newSubstepId,
  newSubstep,
  oldSubstep,
  subStepUId,
  updatePresetTOinstructionMutation
}: {
  oldRecipe: Recipe,
  recipeVariationId: string,
  stepId: string,
  oldSubstepId: string,
  newSubstepId: string,
  newSubstep: Substep,
  oldSubstep: Substep,
  subStepUId: string,
  updatePresetTOinstructionMutation: any
}) => {
  const recipeVariationSteps = getSelectedRecipeVariationSteps(oldRecipe, recipeVariationId);
  const recipeVarStep = getSelectedStep(recipeVariationSteps, stepId);
  const selectedRecipeVariation = getSelectedRecipeVariation(oldRecipe, recipeVariationId);

  if (recipeVarStep) {
    const stepSubStepToUpdate = recipeVarStep.StepsE3.Steps_Substeps.find((stepSubstep) => stepSubstep.id === subStepUId);

    if (stepSubStepToUpdate) {
      const cookingTimeOldSubstep = Math.ceil(cookingTimeCalculation([oldSubstep]) / 60);
      const cookingTimeNewSubstep = Math.ceil(cookingTimeCalculation([newSubstep]) / 60);
      const updatedCookingTime = selectedRecipeVariation ? selectedRecipeVariation.cooking_time - cookingTimeOldSubstep + cookingTimeNewSubstep : 0;
      const { Recipe_Presets, SubSteps_Ingredients_E3s, ...substepInsert } = newSubstep;

      await updatePresetTOinstructionMutation({
        variables: {
          newSubstep: substepInsert,
          subStep_id: newSubstepId,
          stepSubstepId: stepSubStepToUpdate.id,
          cooking_time: updatedCookingTime,
          recipeVariationId
        }
      })
    }
  }
}

const presetTOoutsideInstruction = async ({
  oldRecipe,
  recipeVariationId,
  stepId,
  oldSubstepId,
  newSubstepId,
  newSubstep,
  oldSubstep,
  subStepUId,
  updatePresetTOoutsideInstructionMutation
}: {
  oldRecipe: Recipe,
  recipeVariationId: string,
  stepId: string,
  oldSubstepId: string,
  newSubstepId: string,
  newSubstep: Substep,
  oldSubstep: Substep,
  subStepUId: string,
  updatePresetTOoutsideInstructionMutation: any
}) => {
  const recipeVariationSteps = getSelectedRecipeVariationSteps(oldRecipe, recipeVariationId);
  const recipeVarStep = getSelectedStep(recipeVariationSteps, stepId);
  const selectedRecipeVariation = getSelectedRecipeVariation(oldRecipe, recipeVariationId);

  if (recipeVarStep) {
    const stepSubStepToUpdate = recipeVarStep.StepsE3.Steps_Substeps.find((stepSubstep) => stepSubstep.id === subStepUId);

    if (stepSubStepToUpdate) {
      const cookingTimeOldSubstep = Math.ceil(cookingTimeCalculation([oldSubstep]) / 60);
      const updatedCookingTime = selectedRecipeVariation ? selectedRecipeVariation.cooking_time - cookingTimeOldSubstep : 0;
      const { Recipe_Presets, SubSteps_Ingredients_E3s, ...substepInsert } = newSubstep;

      await updatePresetTOoutsideInstructionMutation({
        variables: {
          newSubstep: substepInsert,
          subStep_id: newSubstepId,
          stepSubstepId: stepSubStepToUpdate.id,
          cooking_time: updatedCookingTime,
          recipeVariationId
        }
      })
    }
  }
}

const presetTOingredients = async ({
  stepId,
  oldSubstepId,
  oldSubstep,
  oldRecipe,
  recipeVariationId,
  newSubstep,
  subStepUId,
  presetTOingredientsMutation,
  fetchRecipeIngredients,
  ingredientsObject
}: {
  stepId: string,
  oldSubstepId: string,
  oldSubstep: Substep,
  oldRecipe: Recipe,
  recipeVariationId: string,
  newSubstep: Substep,
  subStepUId: string,
  presetTOingredientsMutation: any,
  fetchRecipeIngredients: any,
  ingredientsObject: IngredientsInterface
}) => {
  const selectedRecipeVariation = getSelectedRecipeVariation(oldRecipe, recipeVariationId);
  const oldRecipeVariationIngredientList = selectedRecipeVariation?.ingredients ? [...selectedRecipeVariation.ingredients] : [];

  const recipeVariationSteps = getSelectedRecipeVariationSteps(oldRecipe, recipeVariationId);
  const recipeVarStep = getSelectedStep(recipeVariationSteps, stepId);
  const currentMetadata = selectedRecipeVariation?.metadata || {};
  const updatedIngredientsObject = await ensureIngredientsObject(
    ingredientsObject,
    newSubstep,
    fetchRecipeIngredients
  );
  const updatedNutrientInfo = getNutrientInfo(oldRecipe, updatedIngredientsObject, newSubstep, oldSubstep);
  const updatedMetadata = { ...currentMetadata, nutritionalValues: updatedNutrientInfo};
  
  const updatedCookingTime = selectedRecipeVariation && selectedRecipeVariation?.cooking_time - Math.ceil(cookingTimeCalculation([oldSubstep]) / 60);
  const { newIngredientsList, ...parametersWithoutIngredientList } = newSubstep.parameters;
  const { Recipe_Presets, SubSteps_Ingredients_E3s: substepIngredientInsert, ...substepInsert } = newSubstep;
  substepInsert.parameters = parametersWithoutIngredientList;
  substepIngredientInsert.forEach((substepIngredient) => {
    oldRecipeVariationIngredientList.push(substepIngredient.ingredient_id);
  })
  const ingredientIdList = Array.from(new Set(oldRecipeVariationIngredientList));

  generateIngredientImages(newIngredientsList)

  const ingredientsInsert = ingredientInsertsVals(newIngredientsList)

  if (recipeVarStep) {
    const stepSubStepToUpdate = recipeVarStep.StepsE3.Steps_Substeps.find((stepSubstep) => stepSubstep.id === subStepUId);
    if (stepSubStepToUpdate) {
      await presetTOingredientsMutation({
        variables: {
          subStep_id: newSubstep.id,
          newSubstep: substepInsert,
          ingredientsInsert,
          substepIngredientIdsToAdd: substepIngredientInsert,
          recipeVariationId,
          newIngredientsList: ingredientIdList,
          cooking_time: updatedCookingTime,
          stepSubstepId: stepSubStepToUpdate.id,
          updatedMetadata
        }
      })
    }
  }
}

const presetTOpreset = async ({
  oldRecipe,
  recipeVariationId,
  stepId,
  oldSubstepId,
  newSubstepId,
  newSubstep,
  oldSubstep,
  subStepUId,
  updatePresetTOpresetMutation
}: {
  oldRecipe: Recipe,
  recipeVariationId: string,
  stepId: string,
  oldSubstepId: string,
  newSubstepId: string,
  newSubstep: Substep,
  oldSubstep: Substep,
  subStepUId: string,
  updatePresetTOpresetMutation: any
}) => {
  const recipeVariationSteps = getSelectedRecipeVariationSteps(oldRecipe, recipeVariationId);
  const recipeVarStep = getSelectedStep(recipeVariationSteps, stepId);
  const selectedRecipeVariation = getSelectedRecipeVariation(oldRecipe, recipeVariationId);

  if (recipeVarStep) {
    const stepSubStepToUpdate = recipeVarStep.StepsE3.Steps_Substeps.find((stepSubstep) => stepSubstep.id === subStepUId);

    if (stepSubStepToUpdate) {
      const cookingTimeOldSubstep = Math.ceil(cookingTimeCalculation([oldSubstep]) / 60);
      const cookingTimeNewSubstep = Math.ceil(cookingTimeCalculation([newSubstep]) / 60);
      const updatedCookingTime = selectedRecipeVariation ? selectedRecipeVariation.cooking_time - cookingTimeOldSubstep + cookingTimeNewSubstep : 0;
      await updatePresetTOpresetMutation({
        variables: {
          subStep_id: newSubstepId,
          stepSubstepId: stepSubStepToUpdate.id,
          cooking_time: updatedCookingTime,
          recipeVariationId
        }
      })
    }
  }
}

const useSaveSubstep = () => {
  const [addPresetMutation, { loading: addingPreset }] = useMutation(ADD_PRESET);
  const [addInstructionMutation, { loading: addingInstuction }] = useMutation(ADD_INSTRUCTION);
  const [addIngredientMutation, { loading: addingIngredient }] = useMutation(ADD_INGREDIENT);
  const [addOutsideInstructionMutation, { loading: addingOutsideInstruction }] = useMutation(ADD_OUTSIDE_INSTRUCTION);

  const [initializePresetMutation, { loading: initializingPreset }] = useMutation(INITIALIZE_PRESET);
  const [initializeInstructionMutation, { loading: initializingInstruction }] = useMutation(INITIALIZE_INSTRUCTION);
  const [initializeIngredientMutation, { loading: initializingIngredient }] = useMutation(INITIALIZE_INGREDIENT);
  const [initializeOutsideInstructionMutation, { loading: initializingOutsideInstruction }] = useMutation(INITIALIZE_OUTSIDE_INSTRUCTION);

  const [updateInstructionToPreset, { loading: updatingInstructionToPreset }] = useMutation(INSTRUCTION_TO_PRESET);
  const [updateInstructionToOutsideInstruction, { loading: updatingInstructionToOutsideInstruction }] = useMutation(INSTRUCTION_TO_OUTSIDE_INSTRUCTION);
  const [updateInstructionToIngredents, { loading: updatingInstructionToIngredient }] = useMutation(INSTRUCTION_TO_INGREDIENT);
  const [updateInstructionToInstruction, { loading: updatingInstructionToInstruction }] = useMutation(INSTRUCTION_TO_INSTRUCTION);

  const [updateOutsideInstructionToInstruction, { loading: updatingOutsideInstructionToInstruction }] = useMutation(OUTSIDE_INSTRUCTION_TO_INSTRUCTION);
  const [updateOutsideInstructionToPreset, { loading: updatingOutsideInstructionToPreset }] = useMutation(OUTSIDE_INSTRUCTION_TO_PRESET);
  const [updateOutsideInstructionToIngredients, { loading: updatingOutsideInstructionToIngredient }] = useMutation(OUTSIDE_INSTRUCTION_TO_INGREDIENT);
  const [updateOutsideInstructionToOutsideInstruction, { loading: updatingOutsideInstructionToOutsideInstruction }] = useMutation(OUTSIDE_INSTRUCTION_TO_OUTSIDE_INSTRUCTION);

  const [updatePresetTOinstructionMutation, { loading: updatingPresetTOinstructionMutation }] = useMutation(PRESET_TO_INSTRUCTION);
  const [updatePresetTOoutsideInstructionMutation, { loading: updatingPresetTOoutsideInstructionMutation }] = useMutation(PRESET_TO_OUTSIDE_INSTRUCTION);
  const [presetTOingredientsMutation, { loading: updatingPresetToIngredientMutation }] = useMutation(PRESET_TO_INGREDIENT);
  const [updatePresetTOpresetMutation, { loading: updatingPresetTOpresetMutation }] = useMutation(PRESET_TO_PRESET);

  const [updateIngredientTOinstruction, { loading: updatingIngredientToInstruction }] = useMutation(INGREDIENT_TO_INSTRUCTION);
  const [updateIngredientTOpreset, { loading: updatingIngredientToPreset }] = useMutation(INGREDIENT_TO_PRESET);
  const [updateIngredientTOoutsideInstruction, { loading: updatingIngredientToOutsideInstruction }] = useMutation(INGREDIENT_TO_OUTSIDE_INSTRUCTION);
  const [updateIngredientTOingredients, { loading: updatingIngredientToIngredient }] = useMutation(INGREDIENT_TO_INGREDIENT);
  const [updateLastUpdatedAtAndTags, { loading: updatingRecipeLastUpdatedAtAndTags }] = useMutation(UPDATE_RECIPE_LAST_UPDATED_AT_AND_TAGS);

  const [fetchRecipeIngredients, { loading: fetchingIngredients }] = useLazyQuery(GET_RECIPE_INGREDIENTS)
  
  const ingredientsObject = useAppSelector(ingredientsObjectSelector);

  const onSave = async ({
    oldRecipe,
    stepId,
    recipeVariationId,
    oldSubstep,
    newSubstep,
    addSubstep,
    addSubStepAt,
    initializeStep,
    subStepUId,
    onCompleted,
    onError,
  }: {
    oldRecipe: Recipe,
    stepId: string,
    recipeVariationId: string,
    oldSubstep?: Substep,
    newSubstep: Substep,
    addSubstep: boolean,
    addSubStepAt?: number,
    initializeStep: boolean,
    subStepUId: string,
    onCompleted: () => Promise<any>,
    onError: (err: any) => void
  }) => {
    try {

      const { id: oldSubstepId, type: oldSubstepType } = oldSubstep ?? {};
      const { id: newSubstepId, type: newSubstepType } = newSubstep;

      const isOldSubstepPreset = oldSubstep ? oldSubstep.Recipe_Presets.length > 0 : false;
      const isNewSubstepPreset = newSubstep.Recipe_Presets.length > 0;
      switch (chooseSaveTypeChange({ oldSubstepType, newSubstepType, isOldSubstepPreset, isNewSubstepPreset, addSubstep, initializeStep })) {
        // add new substep
        case "addPreset":
          if (addSubStepAt !== undefined) {
            await addPreset({
              oldRecipe,
              recipeVariationId,
              stepId,
              addSubStepAt,
              newSubstepId,
              newSubstep,
              addPresetMutation
            });
          }
          break;
        case "addInstruction":
          if (addSubStepAt !== undefined) {
            await addInstruction({
              oldRecipe,
              recipeVariationId,
              stepId,
              addSubStepAt,
              newSubstepId,
              newSubstep,
              addInstructionMutation
            });
          }
          break;
        case "addIngredient":
          if (addSubStepAt !== undefined) {
            await addIngredient({
              oldRecipe,
              recipeVariationId,
              stepId,
              addSubStepAt,
              newSubstepId,
              newSubstep,
              addIngredientMutation,
              fetchRecipeIngredients,
              ingredientsObject
            });
          }
          break;
        case "addOutsideInstruction":
          if (addSubStepAt !== undefined) {
            await addOutsideInstruction({
              oldRecipe,
              recipeVariationId,
              stepId,
              addSubStepAt,
              newSubstepId,
              newSubstep,
              addOutsideInstructionMutation
            });
          }
          break;

        // initialize new step  
        case "initializePreset":
          await initializePreset({
            stepId,
            newSubstepId,
            recipeVariationId,
            newSubstep,
            initializePresetMutation
          });
          break;
        case "initializeInstruction":
          await initializeInstruction({
            stepId,
            newSubstepId,
            recipeVariationId,
            newSubstep,
            initializeInstructionMutation
          });
          break;
        case "initializeIngredient":
          await initializeIngredient({
            oldRecipe,
            recipeVariationId,
            stepId,
            newSubstepId,
            newSubstep,
            initializeIngredientMutation,
            fetchRecipeIngredients,
            ingredientsObject
          });
          break;
        case "initializeOutsideInstruction":
          await initializeOutsideInstruction({
            stepId,
            newSubstepId,
            recipeVariationId,
            newSubstep,
            initializeOutsideInstructionMutation
          });
          break;

        // change from inghandleRecipeCreationModeredient
        case "ingredientsTOinstruction":
          if (oldSubstepId && oldSubstep) {
            await ingredientsTOinstruction({
              oldRecipe,
              recipeVariationId,
              oldSubstepId,
              subStepUId,
              newSubstep,
              oldSubstep,
              updateIngredientTOinstruction,
              ingredientsObject
            });
          }
          break;
        case "ingredientsTOpreset":
          if (oldSubstepId && oldSubstep) {
            await ingredientsTOpreset({
              oldRecipe,
              recipeVariationId,
              oldSubstepId,
              newSubstepId,
              newSubstep,
              oldSubstep,
              stepId,
              subStepUId,
              updateIngredientTOpreset,
              ingredientsObject
            });
          }
          break;
        case "ingredientsTOoutsideInstruction":
          if (oldSubstepId && oldSubstep) {
            await ingredientsTOoutsideInstruction({
              oldRecipe,
              recipeVariationId,
              oldSubstepId,
              subStepUId,
              newSubstep,
              oldSubstep,
              updateIngredientTOoutsideInstruction,
              ingredientsObject
            });
          }
          break;
        case "ingredientsTOingredients":
          if (oldSubstepId && oldSubstep) {
            await ingredientsTOingredients({
              oldRecipe,
              recipeVariationId,
              oldSubstepId,
              subStepUId,
              newSubstep,
              oldSubstep,
              updateIngredientTOingredients,
              ingredientsObject
            });
          }
          break;

        // change from instruction
        case "instructionTOingredients":
          if (oldSubstepId && oldSubstep) {
            await instructionTOingredients({
              oldSubstepId,
              oldSubstep,
              oldRecipe,
              recipeVariationId,
              newSubstep,
              updateInstructionToIngredents,
              fetchRecipeIngredients,
              ingredientsObject
            });
          }
          // PENDING : not implemented
          break;
        case "instructionTOpreset":
          if (oldSubstepId && oldSubstep) {
            await instructionTOpreset({
              oldRecipe,
              recipeVariationId,
              stepId,
              oldSubstepId,
              newSubstepId,
              newSubstep,
              oldSubstep,
              subStepUId,
              updateInstructionToPreset
            });
          }
          break;
        case "instructionTOoutsideInstruction":
          if (oldSubstepId && oldSubstep) {
            await instructionTOoutsideInstruction({
              oldRecipe,
              recipeVariationId,
              oldSubstepId,
              newSubstep,
              oldSubstep,
              updateInstructionToOutsideInstruction
            });
          }
          break;
        case "instructionTOinstruction":
          if (oldSubstepId && oldSubstep) {
            await instructionTOinstruction({
              oldRecipe,
              recipeVariationId,
              oldSubstepId,
              newSubstep,
              oldSubstep,
              updateInstructionToInstruction
            });
          }
          break;

        // change from outsideInstruction
        case "outsideInstructionTOinstruction":
          if (oldSubstepId) {
            await outsideInstructionTOinstruction({
              oldRecipe,
              recipeVariationId,
              oldSubstepId,
              newSubstep,
              updateOutsideInstructionToInstruction
            });
          }
          break;
        case "outsideInstructionTOpreset":
          if (oldSubstepId) {
            await outsideInstructionTOpreset({
              oldRecipe,
              recipeVariationId,
              oldSubstepId,
              newSubstepId,
              newSubstep,
              stepId,
              subStepUId,
              updateOutsideInstructionToPreset
            });
          }
          break;
        case "outsideInstructionTOingredients":
          if (oldSubstepId) {
            await outsideInstructionTOingredients({
              oldSubstepId,
              oldRecipe,
              recipeVariationId,
              newSubstep,
              updateOutsideInstructionToIngredients,
              fetchRecipeIngredients,
              ingredientsObject
            });
          }
          break;
        case "outsideInstructionTOoutsideInstruction":
          if (oldSubstepId) {
            await outsideInstructionTOoutsideInstruction({
              oldSubstepId,
              newSubstep,
              updateOutsideInstructionToOutsideInstruction
            });
          }
          break;

        // change from preset
        case "presetTOinstruction":
          if (oldSubstepId && oldSubstep) {
            await presetTOinstruction({
              oldRecipe,
              recipeVariationId,
              stepId,
              oldSubstepId,
              newSubstepId,
              newSubstep,
              oldSubstep,
              subStepUId,
              updatePresetTOinstructionMutation
            });
          }
          break;
        case "presetTOoutsideInstruction":
          if (oldSubstepId && oldSubstep) {
            await presetTOoutsideInstruction({
              oldRecipe,
              recipeVariationId,
              stepId,
              oldSubstepId,
              newSubstepId,
              newSubstep,
              oldSubstep,
              subStepUId,
              updatePresetTOoutsideInstructionMutation
            });
          }
          break;
        case "presetTOingredients":
          if (oldSubstepId && oldSubstep) {
            await presetTOingredients({
              stepId,
              oldSubstepId,
              oldSubstep,
              oldRecipe,
              recipeVariationId,
              newSubstep,
              subStepUId,
              presetTOingredientsMutation,
              fetchRecipeIngredients,
              ingredientsObject
            });
          }
          break;
        case "presetTOpreset":
          if (oldSubstepId && oldSubstep) {
            await presetTOpreset({
              oldRecipe,
              recipeVariationId,
              stepId,
              oldSubstepId,
              newSubstepId,
              newSubstep,
              oldSubstep,
              subStepUId,
              updatePresetTOpresetMutation
            });
          }

          break;
        default:
          break;
      }
      await updateRecipeLastUpdatedAtAndTags({ oldRecipe, updateLastUpdatedAtAndTags });

      await onCompleted();

    } catch (err) {
      console.error("error saving substep", err)
      onError(err);
    }
  };

  return {
    onSave,
    loading: addingPreset ||
      addingInstuction ||
      addingOutsideInstruction ||
      addingIngredient ||
      initializingPreset ||
      initializingInstruction ||
      initializingOutsideInstruction ||
      initializingIngredient ||
      updatingInstructionToPreset ||
      updatingInstructionToOutsideInstruction ||
      updatingInstructionToInstruction ||
      updatingInstructionToIngredient ||
      updatingOutsideInstructionToInstruction ||
      updatingOutsideInstructionToPreset ||
      updatingOutsideInstructionToOutsideInstruction ||
      updatingOutsideInstructionToIngredient ||
      updatingPresetTOinstructionMutation ||
      updatingPresetTOoutsideInstructionMutation ||
      updatingPresetToIngredientMutation ||
      updatingPresetTOpresetMutation ||
      updatingIngredientToInstruction ||
      updatingIngredientToPreset ||
      updatingIngredientToOutsideInstruction ||
      updatingIngredientToIngredient ||
      updatingRecipeLastUpdatedAtAndTags ||
      fetchingIngredients
  };
}

export default useSaveSubstep